import {Component, ElementRef, Inject, LOCALE_ID, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {User} from '../../model/auth/User';
import {ProgressRef, ProgressService} from '../../service/progress.service';
import {CustomValidators, MismatchErrorStateMatcher} from '../../helper/custom.validators';
import {AuthService} from '../../service/auth.service';
import {NotificationHandler} from '../../helper/notification.handler';
import {ALL_LOCALES, Locale, localizeLocale} from '../../model/Locale';
import {LanguageSwitchComponent} from '../../common/language-switch/language-switch.component';
import {Router} from '@angular/router';
import {UrlHelper} from '../../helper/url.helper';
import {UserService} from '../../service/user.service';
import {Session} from '../../model/auth/Session';
import {localizeRole, Role} from '../../model/auth/Role';

@Component({
  selector: 'profile-details',
  templateUrl: './profile-details.component.html',
  styleUrls: ['./profile-details.component.scss']
})
export class ProfileDetailsComponent implements OnInit {

  readonly locales = ALL_LOCALES;
  readonly userRoles = Role;
  readonly requirePhone = [Role.Host, Role.Coordinator, Role.Teacher];

  user!: User | null;
  session!: Session | null;
  role?: Role;

  emailChangeAllowed = false;
  emailUnique = false;

  dataLoadingProgress: ProgressRef | null | undefined;
  @ViewChild('progressContainer', {static: true}) progressContainer!: ElementRef;

  emailField = new FormControl('', [
    Validators.email, Validators.required, Validators.minLength(5), Validators.maxLength(128)
  ]);
  phoneField = new FormControl('', [
    CustomValidators.phoneNumber, Validators.minLength(7), Validators.maxLength(15)
  ]);
  firstNameField = new FormControl('', [
    Validators.required, Validators.minLength(2), Validators.maxLength(128)
  ]);
  lastNameField = new FormControl('', [
    Validators.required, Validators.minLength(2), Validators.maxLength(128)
  ]);
  localeField = new FormControl('', [
    Validators.required
  ]);

  roleField = new FormControl({value: '', disabled: true});

  profileDetailsForm: FormGroup = new FormGroup({
    email: this.emailField,
    phone: this.phoneField,
    firstName: this.firstNameField,
    lastName: this.lastNameField,
    locale: this.localeField
  });

  oldLocale: Locale;

  oldPassField = new FormControl('', [Validators.required]);
  passwordField = new FormControl('', [CustomValidators.password]);
  confirmPassField = new FormControl('', [Validators.required]);

  changePassForm: FormGroup;
  matcher = new MismatchErrorStateMatcher();

  get firstNameEditTooltipSocial() {
    return $localize`:User Profile|User profile FirstName Edit Tooltip Social@@user-profile-first-name-edit-tooltip-social:First name cannot be edited when signing in with `;
  }

  get lastNameEditTooltipSocial() {
    return $localize`:User Profile|User profile LastName Edit Tooltip Social@@user-profile-last-name-edit-tooltip-social:Last name cannot be edited when signing in with `;
  }

  constructor(private progressService: ProgressService,
              private authService: AuthService,
              private userService: UserService,
              private router: Router,
              formBuilder: FormBuilder,
              @Inject(LOCALE_ID) locale: string) {

    this.changePassForm = formBuilder.group({
      oldPass: this.oldPassField,
      password: this.passwordField,
      confirmPass: this.confirmPassField
    }, {validators: CustomValidators.passwordsMatch});

    this.oldLocale = locale as Locale;
  }

  async ngOnInit() {
    this.startProgress();
    await this.getUser();
    this.stopProgress();
  }

  private startProgress() {
    this.dataLoadingProgress = this.progressService.showProgress(this.progressContainer);
  }

  private stopProgress() {
    if (this.dataLoadingProgress) {
      this.progressService.detach(this.dataLoadingProgress);
      this.dataLoadingProgress = null;
    }
  }

  compareLocales(l1: Locale, l2: Locale) {
    return l1 == l2;
  }

  displayLocale(locale: Locale) {
    return localizeLocale(locale);
  }

  private async getUser() {
    this.user = await this.authService.getUser();
    this.session = await this.authService.getSession();

    if (this.session) {
      this.role = this.session.role;
      if (this.role) this.roleField.setValue(localizeRole(this.role));

      if (this.session.role && this.requirePhone.includes(this.session.role)) {
        this.phoneField.setValidators([
          Validators.required, CustomValidators.phoneNumber, Validators.minLength(7), Validators.maxLength(15)
        ]);
        this.phoneField.updateValueAndValidity();
      }
    }

    if (this.user) {
      if (this.user.username && !this.user.email) {
        this.emailChangeAllowed = true;
      }

      this.emailField.setValue(this.user.email);
      if (!this.emailChangeAllowed) {
        this.emailField.disable();
      }

      this.phoneField.setValue(this.user.phone);
      this.firstNameField.setValue(this.user.firstName);
      this.lastNameField.setValue(this.user.lastName);

      if (this.user.locale) {
        this.localeField.setValue(this.user.locale);
        this.oldLocale = this.user.locale;
      } else {
        this.localeField.setValue(this.oldLocale);
      }

      if (this.user.provider) {
        this.firstNameField.disable();
        this.lastNameField.disable();
      }
    }
  }

  proceedAllowed(): boolean {
    return !this.dataLoadingProgress
      && this.user != null
      && (this.user.provider !== undefined
        || (this.firstNameField.valid && this.lastNameField.valid && this.phoneField.valid &&
          (!this.emailChangeAllowed || (this.emailField.valid && this.emailUnique))));
  }

  async updateProfile() {
    this.startProgress();

    if (this.user && !this.user.provider) {
      const user = new User();
      user.phone = this.phoneField.value as string;
      user.firstName = !this.user.provider ? this.firstNameField.value as string : this.user.firstName;
      user.lastName = !this.user.provider ? this.lastNameField.value as string : this.user.lastName;
      user.locale = this.localeField.value as Locale;

      if (this.emailChangeAllowed) user.email = (this.emailField.value as string).trim();

      const oldLocale = this.oldLocale;

      const updated = await this.authService.updateUserProfile(user, this.emailChangeAllowed);
      if (updated) {
        await this.getUser();
        this.userService.reportUpdateUser(this.user.id).subscribe(() => {
        }, error => {
          NotificationHandler.handleError($localize`:User Profile|User profile update report failed notification@@user-profile-report-failed-notification:Profile update report failed`, error);
        }, () => {
          NotificationHandler.notification($localize`:User Profile|User profile updated notification@@user-profile-updated-notification:Profile was updated successfully`);

          if (this.user && this.user.locale !== oldLocale) {
            LanguageSwitchComponent.switchLanguage(this.user.locale as string);
          }
          this.stopProgress();
        });
      } else {
        this.stopProgress();
      }
    }
  }

  async changePassword() {
    this.startProgress();

    const changed = await this.authService.changePassword(this.oldPassField.value, this.passwordField.value);
    if (changed) {
      NotificationHandler.notification($localize`:User Profile|User profile password changed notification@@user-profile-password-changed-notification:Password was changed successfully`);
    }

    this.stopProgress();
  }

  async verifyEmail() {
    if (this.user) {
      await this.authService.verifyCurrentUserAttribute('email');

      NotificationHandler.notification($localize`:Sign Up Confirm|Code re-send confirmation message@@sign-up-code-re-send-confirmation-message:A code has been emailed to you`);

      await this.router.navigate([`${UrlHelper.SIGN_UP_CONFIRM_PATH}${this.user.email}/${this.user.id}`]);
    }
  }

  searchUserByEmail = () => {
    this.verifyUserExists();
  };

  verifyUserExists() {
    this.emailUnique = false;
    if (this.emailField.value && this.emailField.valid) {
      this.userService.userExistsAuth(this.emailField.value).subscribe(user => {
        if (!user.id) {
          this.emailUnique = true;
        } else {
          this.emailField.setErrors({
            unique: {
              error: $localize`:User Profile|User profile email not unique notification@@user-profile-email-not-unique-notification:This email is already registered in a system`
            }
          });
        }
      });
    }
  }
}
